import {
  type BrandConfig,
  type BrandIcons,
  type BrandId,
  BRANDS,
  DEFAULT_BRAND_ID,
} from "../config/brands.ts";
import { DISCOVERY_URL } from "./urls.ts";

let cachedBrand: BrandConfig | null = null;

/**
 * Resolve the active brand from the domain being served.
 * A `?brand=propti` query parameter overrides detection (useful on
 * localhost/previews). Defaults to dammi.
 */
export function detectBrandId(
  hostname: string = location.hostname,
  search: string = location.search,
): BrandId {
  const override = new URLSearchParams(search).get("brand");
  if (override && override in BRANDS) {
    return override as BrandId;
  }

  const host = hostname.toLowerCase();
  for (const brand of Object.values(BRANDS)) {
    if (brand.hostnames.some((h) => host === h || host.endsWith(`.${h}`))) {
      return brand.id;
    }
  }
  return DEFAULT_BRAND_ID;
}

export function getBrand(): BrandConfig {
  cachedBrand ??= BRANDS[detectBrandId()];
  return cachedBrand;
}

/** Apply the active brand's text replacements to a string. */
export function brandText(text: string): string {
  let result = text;
  for (const { pattern, replacement } of getBrand().replacements) {
    result = result.replace(pattern, replacement);
  }
  return result;
}

/** Brand-specific email address, e.g. brandEmail("info") -> "info@dammi.lv". */
export function brandEmail(localPart: string): string {
  return `${localPart}@${getBrand().domain}`;
}

/**
 * Tag the document with the active brand so the CSS theme overrides in
 * input.css (:root[data-brand="..."]) kick in, and sync the theme-color meta.
 */
export function applyBrandTheme(): void {
  const brand = getBrand();
  document.documentElement.dataset.brand = brand.id;
  document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
    .forEach((meta) => {
      meta.content = brand.themeColor;
    });
  if (brand.icons) applyBrandIcons(brand.icons);
}

/** Replace the favicon links ("icon", "shortcut icon", "apple-touch-icon"). */
function applyBrandIcons(icons: BrandIcons): void {
  document.querySelectorAll('link[rel*="icon"], link[rel="manifest"]')
    .forEach((link) => link.remove());

  const links: Record<string, string>[] = [
    { rel: "icon", href: icons.ico, sizes: "48x48" },
    { rel: "icon", href: icons.svg, type: "image/svg+xml" },
    { rel: "apple-touch-icon", href: icons.appleTouch },
    { rel: "manifest", href: icons.manifest },
  ];
  for (const attrs of links) {
    const link = document.createElement("link");
    for (const [name, value] of Object.entries(attrs)) {
      link.setAttribute(name, value);
    }
    document.head.appendChild(link);
  }
}

/**
 * Rewrite brand references in the rendered document: title, SEO/social meta
 * tags, canonical/alternate links, mailto/site links and visible text.
 * Language switching re-applies its own texts through brandText (see
 * utils/lang.ts), so both stay consistent.
 */
export function applyBrandContent(): void {
  if (getBrand().replacements.length === 0) return;

  document.title = brandText(document.title);

  document.querySelectorAll<HTMLMetaElement>(
    'meta[name="description"], meta[name="keywords"], meta[name="author"], meta[property^="og:"], meta[name^="twitter:"]',
  ).forEach((meta) => {
    meta.content = brandText(meta.content);
  });

  document.querySelectorAll<HTMLLinkElement>(
    'link[rel="canonical"], link[rel="alternate"]',
  ).forEach((link) => {
    const href = link.getAttribute("href");
    if (href) link.setAttribute("href", brandText(href));
  });

  document.querySelectorAll<HTMLAnchorElement>("a[href]").forEach((anchor) => {
    const href = anchor.getAttribute("href");
    if (!href) return;
    // The discovery/booking page is only hosted on the main domain.
    if (href === DISCOVERY_URL || anchor.hasAttribute("data-discovery-link")) {
      return;
    }
    if (href.startsWith("mailto:") || href.startsWith("http")) {
      anchor.setAttribute("href", brandText(href));
    }
  });

  document.querySelectorAll<HTMLImageElement>("img").forEach((img) => {
    for (const attr of ["src", "alt"]) {
      const value = img.getAttribute(attr);
      if (value) img.setAttribute(attr, brandText(value));
    }
  });

  rewriteTextNodes(document.body);
}

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA"]);

function rewriteTextNodes(root: Node): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      SKIP_TAGS.has(node.parentElement?.tagName ?? "")
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT,
  });
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node.nodeValue;
    if (!text) continue;
    const replaced = brandText(text);
    if (replaced !== text) {
      node.nodeValue = replaced;
    }
  }
}
